<template>
  <div class="preproduction-list">
    <div class="page-header">
      <h2>预生产订单管理</h2>
      <a-space>
        <a-button @click="handleExport" :loading="exportLoading">
          <template #icon><DownloadOutlined /></template>
          导出Excel
        </a-button>
        <a-button type="primary" @click="$router.push('/production/preproduction/create')">
          <template #icon>
            <PlusOutlined />
          </template>
          创建预生产订单
        </a-button>
      </a-space>
    </div>

    <a-card class="summary-card" :loading="summaryLoading">
      <a-row :gutter="24">
        <a-col :span="6">
          <a-statistic
            title="总数量"
            :value="summaryData.totalQuantity || 0"
            :value-style="{ fontSize: '28px', fontWeight: 'bold' }"
            suffix="件"
          />
          <div class="stat-sub">共 {{ summaryData.orderCount || 0 }} 个订单</div>
        </a-col>
        <a-col :span="6">
          <a-statistic
            title="已完成"
            :value="summaryData.completedQuantity || 0"
            :value-style="{ color: '#52c41a', fontSize: '28px', fontWeight: 'bold' }"
            suffix="件"
          />
          <div class="stat-sub">{{ summaryData.completedCount || 0 }} 个订单已完成</div>
        </a-col>
        <a-col :span="6">
          <a-statistic
            title="待完成"
            :value="summaryData.remainingQuantity || 0"
            :value-style="{ color: '#1890ff', fontSize: '28px', fontWeight: 'bold' }"
            suffix="件"
          />
          <div class="stat-sub">{{ summaryData.inProgressCount || 0 }} 进行中 / {{ summaryData.pendingCount || 0 }} 待处理</div>
        </a-col>
        <a-col :span="6">
          <a-statistic
            title="完成率"
            :value="completionRate"
            :value-style="{ color: completionRate >= 80 ? '#52c41a' : completionRate >= 50 ? '#faad14' : '#ff4d4f', fontSize: '28px', fontWeight: 'bold' }"
            suffix="%"
          />
          <div class="stat-sub" v-if="summaryData.cancelledCount > 0">{{ summaryData.cancelledCount }} 个已取消</div>
        </a-col>
      </a-row>
    </a-card>

    <a-card class="filter-card">
      <a-form layout="inline">
        <a-form-item label="订单编号">
          <a-input
            v-model:value="searchForm.preProductionNo"
            placeholder="请输入订单编号"
            allow-clear
            style="width: 200px"
          />
        </a-form-item>
        <a-form-item label="工厂">
          <a-select
            v-model:value="searchForm.factoryId"
            placeholder="请选择工厂"
            allow-clear
            style="width: 200px"
          >
            <a-select-option
              v-for="factory in factories"
              :key="factory.id"
              :value="factory.id"
            >
              {{ factory.name }}
            </a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="状态">
          <a-select
            v-model:value="searchForm.status"
            placeholder="全部"
            allow-clear
            style="width: 120px"
          >
            <a-select-option value="">全部</a-select-option>
            <a-select-option value="PENDING">待处理</a-select-option>
            <a-select-option value="IN_PROGRESS">进行中</a-select-option>
            <a-select-option value="COMPLETED">已完成</a-select-option>
            <a-select-option value="CANCELLED">已取消</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="付款状态">
          <a-select
            v-model:value="searchForm.paymentStatus"
            placeholder="全部"
            allow-clear
            style="width: 120px"
          >
            <a-select-option value="">全部</a-select-option>
            <a-select-option value="UNPAID">未付款</a-select-option>
            <a-select-option value="PAID">已付款</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="日期范围">
          <a-range-picker
            v-model:value="searchForm.dateRange"
            format="YYYY-MM-DD"
            style="width: 250px"
          />
        </a-form-item>
        <a-form-item label="商品名称">
          <a-input
            v-model:value="searchForm.productName"
            placeholder="请输入商品名称"
            allow-clear
            style="width: 200px"
          />
        </a-form-item>
        <a-form-item>
          <a-space>
            <a-button type="primary" @click="handleSearch">
              <template #icon>
                <SearchOutlined />
              </template>
              查询
            </a-button>
            <a-button @click="handleReset">
              重置
            </a-button>
          </a-space>
        </a-form-item>
      </a-form>
    </a-card>

    <a-card>
      <a-table
        :columns="columns"
        :data-source="orderList"
        :loading="loading"
        :pagination="pagination"
        @change="handleTableChange"
        row-key="id"
        :scroll="{ x: 1400 }"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'preProductionNo'">
            <a @click="handleViewDetail(record)">{{ record.preProductionNo }}</a>
          </template>
          <template v-if="column.key === 'progress'">
            <a-progress
              :percent="calculateProgress(record)"
              :status="record.status === 'COMPLETED' ? 'success' : 'active'"
              size="small"
            />
            <div style="font-size: 12px; color: #666; margin-top: 4px;">
              {{ record.completedQuantity || 0 }} / {{ record.totalQuantity || 0 }}
            </div>
          </template>
          <template v-if="column.key === 'status'">
            <a-tag :color="getStatusColor(record.status)">
              {{ getStatusText(record.status) }}
            </a-tag>
          </template>
          <template v-if="column.key === 'paymentStatus'">
            <a-popconfirm
              :title="record.paymentStatus === 'PAID' ? '确定标记为「未付款」吗？' : '确定标记为「已付款」吗？'"
              ok-text="确定"
              cancel-text="取消"
              @confirm="handleTogglePayment(record)"
            >
              <a-tag
                :color="record.paymentStatus === 'PAID' ? 'green' : 'orange'"
                style="cursor: pointer"
              >
                {{ getPaymentStatusText(record.paymentStatus) }}
                <EditOutlined style="margin-left: 4px; font-size: 11px; opacity: 0.65" />
              </a-tag>
            </a-popconfirm>
          </template>
          <template v-if="column.key === 'action'">
            <a-space>
              <a-button type="link" size="small" @click="handleViewDetail(record)">
                查看
              </a-button>
              <a-button
                v-if="record.status !== 'COMPLETED' && record.status !== 'CANCELLED'"
                type="link"
                size="small"
                style="color: #52c41a"
                @click="handleComplete(record)"
              >
                批次入库
              </a-button>
              <a-popconfirm
                v-if="record.status === 'PENDING'"
                title="确定要取消这个订单吗？"
                @confirm="handleCancel(record)"
              >
                <a-button type="link" size="small" danger>
                  取消订单
                </a-button>
              </a-popconfirm>
              <a-popconfirm
                v-if="record.status !== 'COMPLETED'"
                title="确定要删除这个预生产单吗？如果已有入库记录，将会回滚库存变动。"
                @confirm="handleDelete(record)"
                ok-text="确定"
                cancel-text="取消"
              >
                <a-button type="link" size="small" danger>
                  删除
                </a-button>
              </a-popconfirm>
            </a-space>
          </template>
        </template>
      </a-table>
    </a-card>

    <!-- 批次入库Modal -->
    <a-modal
      v-model:open="completeModalVisible"
      title="批次入库"
      @ok="handleCompleteSubmit"
      @cancel="handleCompleteCancel"
      :confirm-loading="submitting"
      width="900px"
    >
      <a-form layout="vertical">
        <a-form-item label="完成日期" required>
          <a-date-picker
            v-model:value="completeForm.completionDate"
            format="YYYY-MM-DD"
            style="width: 200px"
          />
        </a-form-item>
        <a-form-item label="订单明细">
          <a-table
            :columns="completeColumns"
            :data-source="completeForm.items"
            :pagination="false"
            size="small"
            row-key="itemId"
          >
            <template #bodyCell="{ column, record }">
              <template v-if="column.key === 'remainingQuantity'">
                {{ record.plannedQuantity - record.completedQuantity }}
              </template>
              <template v-if="column.key === 'completionQuantity'">
                <a-input-number
                  v-model:value="record.completionQuantity"
                  :min="0"
                  :max="record.plannedQuantity - record.completedQuantity"
                  style="width: 100px"
                />
              </template>
            </template>
          </a-table>
        </a-form-item>
        <a-form-item label="备注">
          <a-textarea
            v-model:value="completeForm.remarks"
            :rows="3"
            placeholder="请输入备注"
          />
        </a-form-item>
        <div style="text-align: right; padding: 10px; background: #f5f5f5; border-radius: 4px;">
          <span style="font-size: 14px;">
            总数量: <span style="font-weight: bold; color: #1890ff;">{{ totalCompletionQuantity }}</span>
          </span>
        </div>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { message } from 'ant-design-vue'
import {
  PlusOutlined,
  SearchOutlined,
  DownloadOutlined,
  EditOutlined
} from '@ant-design/icons-vue'
import dayjs from 'dayjs'
import { preProductionApi, factoryApi } from '@/api'

export default {
  name: 'PreProductionList',
  components: {
    PlusOutlined,
    SearchOutlined,
    DownloadOutlined,
    EditOutlined
  },
  setup() {
    const router = useRouter()
    const loading = ref(false)
    const submitting = ref(false)
    const exportLoading = ref(false)
    const summaryLoading = ref(false)
    const orderList = ref([])
    const factories = ref([])
    const summaryData = ref({})
    const completeModalVisible = ref(false)
    const currentOrder = ref(null)

    const searchForm = reactive({
      preProductionNo: '',
      factoryId: undefined,
      status: '',
      paymentStatus: '',
      dateRange: [],
      productName: ''
    })

    const completeForm = reactive({
      completionDate: dayjs(),
      items: [],
      remarks: ''
    })

    const pagination = reactive({
      current: 1,
      pageSize: 10,
      total: 0,
      showSizeChanger: true,
      showTotal: (total) => `共 ${total} 条`
    })

    const columns = [
      {
        title: '订单编号',
        dataIndex: 'preProductionNo',
        key: 'preProductionNo',
        width: 150,
        fixed: 'left'
      },
      {
        title: '工厂',
        dataIndex: 'factoryName',
        key: 'factoryName',
        width: 80
      },
      {
        title: '订单日期',
        dataIndex: 'preProductionDate',
        key: 'preProductionDate',
        width: 120
      },
      {
        title: '商品数',
        dataIndex: 'totalProductCount',
        key: 'totalProductCount',
        width: 80,
        align: 'center'
      },
      {
        title: 'SKU数',
        dataIndex: 'totalSkuCount',
        key: 'totalSkuCount',
        width: 80,
        align: 'center'
      },
      {
        title: '总数量',
        dataIndex: 'totalQuantity',
        key: 'totalQuantity',
        width: 80,
        align: 'center'
      },
      {
        title: '包含商品',
        dataIndex: 'productNames',
        key: 'productNames',
        width: 200,
        ellipsis: true
      },
      {
        title: '完成进度',
        key: 'progress',
        width: 150
      },
      {
        title: '状态',
        dataIndex: 'status',
        key: 'status',
        width: 100
      },
      {
        title: '付款状态',
        dataIndex: 'paymentStatus',
        key: 'paymentStatus',
        width: 100
      },
      {
        title: '备注',
        dataIndex: 'remark',
        key: 'remark',
        ellipsis: true,
        width: 150
      },
      {
        title: '创建时间',
        dataIndex: 'createTime',
        key: 'createTime',
        width: 160
      },
      {
        title: '操作',
        key: 'action',
        width: 200,
        fixed: 'right'
      }
    ]

    const completeColumns = [
      {
        title: '商品',
        dataIndex: 'productName',
        key: 'productName',
        width: 150
      },
      {
        title: '仓库',
        dataIndex: 'warehouseName',
        key: 'warehouseName',
        width: 100
      },
      {
        title: '颜色',
        dataIndex: 'color',
        key: 'color',
        width: 80
      },
      {
        title: '尺码',
        dataIndex: 'size',
        key: 'size',
        width: 80
      },
      {
        title: '计划数量',
        dataIndex: 'plannedQuantity',
        key: 'plannedQuantity',
        width: 80
      },
      {
        title: '已完成',
        dataIndex: 'completedQuantity',
        key: 'completedQuantity',
        width: 80
      },
      {
        title: '待完成',
        key: 'remainingQuantity',
        width: 80
      },
      {
        title: '本次完成',
        key: 'completionQuantity',
        width: 100
      }
    ]

    const completionRate = computed(() => {
      const total = summaryData.value.totalQuantity || 0
      const completed = summaryData.value.completedQuantity || 0
      if (total === 0) return 0
      return Math.round(completed / total * 100)
    })

    const totalCompletionQuantity = computed(() => {
      return completeForm.items.reduce((sum, item) => sum + (item.completionQuantity || 0), 0)
    })

    const totalCompletionAmount = computed(() => {
      return completeForm.items.reduce((sum, item) => {
        return sum + ((item.completionQuantity || 0) * (item.unitPrice || 0))
      }, 0).toFixed(2)
    })

    const calculateProgress = (record) => {
      if (!record.totalQuantity || record.totalQuantity === 0) return 0
      return Math.round((record.completedQuantity || 0) / record.totalQuantity * 100)
    }

    const getStatusColor = (status) => {
      const colors = {
        PENDING: 'orange',
        IN_PROGRESS: 'blue',
        COMPLETED: 'green',
        CANCELLED: 'red'
      }
      return colors[status] || 'default'
    }

    const getStatusText = (status) => {
      const texts = {
        PENDING: '待处理',
        IN_PROGRESS: '进行中',
        COMPLETED: '已完成',
        CANCELLED: '已取消'
      }
      return texts[status] || status
    }

    const getPaymentStatusText = (paymentStatus) => {
      return paymentStatus === 'PAID' ? '已付款' : '未付款'
    }

    const handleTogglePayment = async (record) => {
      const next = record.paymentStatus === 'PAID' ? 'UNPAID' : 'PAID'
      try {
        const result = await preProductionApi.updatePaymentStatus({
          preProductionId: record.id,
          paymentStatus: next
        })
        if (result.code === '0') {
          record.paymentStatus = next
          message.success(next === 'PAID' ? '已标记为已付款' : '已标记为未付款')
        } else {
          message.error(result.msg || '更新付款状态失败')
        }
      } catch (error) {
        console.error('更新付款状态失败:', error)
        message.error('更新付款状态失败')
      }
    }

    const loadFactories = async () => {
      try {
        const result = await factoryApi.all()
        if (result.code === '0') {
          factories.value = result.data || []
        }
      } catch (error) {
        console.error('加载工厂列表失败:', error)
      }
    }

    const loadSummary = async () => {
      summaryLoading.value = true
      try {
        const params = {
          factoryId: searchForm.factoryId || undefined,
          status: searchForm.status || undefined,
          paymentStatus: searchForm.paymentStatus || undefined,
          productName: searchForm.productName || undefined
        }

        if (searchForm.dateRange && searchForm.dateRange.length === 2) {
          params.startDate = searchForm.dateRange[0].format('YYYYMMDD')
          params.endDate = searchForm.dateRange[1].format('YYYYMMDD')
        }

        const result = await preProductionApi.summary(params)
        if (result.code === '0') {
          summaryData.value = result.data || {}
        }
      } catch (error) {
        console.error('加载汇总数据失败:', error)
      } finally {
        summaryLoading.value = false
      }
    }

    const loadOrders = async () => {
      loading.value = true
      try {
        const params = {
          preProductionNo: searchForm.preProductionNo || undefined,
          factoryId: searchForm.factoryId || undefined,
          status: searchForm.status || undefined,
          paymentStatus: searchForm.paymentStatus || undefined,
          productName: searchForm.productName || undefined,
          pageNo: pagination.current,
          pageSize: pagination.pageSize
        }

        if (searchForm.dateRange && searchForm.dateRange.length === 2) {
          params.startDate = searchForm.dateRange[0].format('YYYYMMDD')
          params.endDate = searchForm.dateRange[1].format('YYYYMMDD')
        }

        const result = await preProductionApi.list(params)
        if (result.code === '0') {
          orderList.value = result.data.records || []
          pagination.total = result.data.total || 0
        } else {
          message.error(result.msg || '加载订单列表失败')
        }
      } catch (error) {
        console.error('加载订单列表失败:', error)
        message.error('加载订单列表失败')
      } finally {
        loading.value = false
      }
    }

    const handleTableChange = (pag) => {
      pagination.current = pag.current
      pagination.pageSize = pag.pageSize
      loadOrders()
    }

    const handleSearch = () => {
      pagination.current = 1
      loadOrders()
      loadSummary()
    }

    const handleReset = () => {
      searchForm.preProductionNo = ''
      searchForm.factoryId = undefined
      searchForm.status = ''
      searchForm.paymentStatus = ''
      searchForm.dateRange = []
      searchForm.productName = ''
      pagination.current = 1
      loadOrders()
      loadSummary()
    }

    const handleExport = async () => {
      exportLoading.value = true
      try {
        const params = {
          factoryId: searchForm.factoryId || undefined,
          status: searchForm.status || undefined,
          paymentStatus: searchForm.paymentStatus || undefined,
          productName: searchForm.productName || undefined
        }

        if (searchForm.dateRange && searchForm.dateRange.length === 2) {
          params.startDate = searchForm.dateRange[0].format('YYYYMMDD')
          params.endDate = searchForm.dateRange[1].format('YYYYMMDD')
        }

        const response = await preProductionApi.export(params)
        const blob = new Blob([response.data], { type: 'application/vnd.ms-excel' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        const now = new Date()
        const timestamp = now.getFullYear().toString() +
          String(now.getMonth() + 1).padStart(2, '0') +
          String(now.getDate()).padStart(2, '0') + '_' +
          String(now.getHours()).padStart(2, '0') +
          String(now.getMinutes()).padStart(2, '0') +
          String(now.getSeconds()).padStart(2, '0')
        link.download = `预生产订单_${timestamp}.xlsx`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        message.success('导出成功')
      } catch (error) {
        console.error('导出失败:', error)
        message.error('导出失败')
      } finally {
        exportLoading.value = false
      }
    }

    const handleViewDetail = (record) => {
      router.push(`/production/preproduction/detail/${record.id}`)
    }

    const handleComplete = async (record) => {
      try {
        loading.value = true
        const result = await preProductionApi.detail(record.id)
        if (result.code === '0') {
          currentOrder.value = result.data
          completeForm.completionDate = dayjs()
          completeForm.remarks = ''
          completeForm.items = result.data.items.map(item => ({
            itemId: item.id,
            productName: item.productName,
            warehouseName: item.warehouseName,
            color: item.color,
            size: item.size,
            plannedQuantity: item.plannedQuantity,
            completedQuantity: item.completedQuantity,
            completionQuantity: undefined,
            unitPrice: 0
          }))
          completeModalVisible.value = true
        }
      } catch (error) {
        console.error('加载订单详情失败:', error)
        message.error('加载订单详情失败')
      } finally {
        loading.value = false
      }
    }

    const calculateTotals = () => {
      // Trigger computed properties update
    }

    const handleCompleteSubmit = async () => {
      if (!completeForm.completionDate) {
        message.error('请选择完成日期')
        return
      }

      const validItems = completeForm.items.filter(item => item.completionQuantity > 0)
      if (validItems.length === 0) {
        message.error('请至少完成一个SKU')
        return
      }

      // 验证完成数量不能超过待完成数量
      const invalidItems = validItems.filter(item => {
        const remaining = item.plannedQuantity - item.completedQuantity
        return item.completionQuantity > remaining
      })

      if (invalidItems.length > 0) {
        const firstInvalid = invalidItems[0]
        const remaining = firstInvalid.plannedQuantity - firstInvalid.completedQuantity
        message.error(`${firstInvalid.productName}（${firstInvalid.color || '默认'}/${firstInvalid.size || '默认'}）的完成数量不能超过待完成数量 ${remaining}`)
        return
      }

      try {
        submitting.value = true
        const result = await preProductionApi.complete({
          preProductionId: currentOrder.value.id,
          completionDate: completeForm.completionDate.format('YYYYMMDD'),
          remark: completeForm.remarks,
          items: validItems.map(item => ({
            preProductionItemId: item.itemId,
            completionQuantity: item.completionQuantity,
            unitPrice: item.unitPrice || 0
          }))
        })

        if (result.code === '0') {
          message.success('批次入库成功')
          completeModalVisible.value = false
          loadOrders()
          loadSummary()
        } else {
          message.error(result.msg || '批次入库失败')
        }
      } catch (error) {
        console.error('批次入库失败:', error)
        message.error('批次入库失败')
      } finally {
        submitting.value = false
      }
    }

    const handleCompleteCancel = () => {
      completeModalVisible.value = false
      currentOrder.value = null
    }

    const handleCancel = async (record) => {
      try {
        const result = await preProductionApi.cancel(record.id)
        if (result.code === '0') {
          message.success('取消订单成功')
          loadOrders()
          loadSummary()
        } else {
          message.error(result.msg || '取消订单失败')
        }
      } catch (error) {
        console.error('取消订单失败:', error)
        message.error('取消订单失败')
      }
    }

    const handleDelete = async (record) => {
      try {
        const result = await preProductionApi.delete(record.id)
        if (result.code === '0') {
          message.success('删除预生产单成功')
          loadOrders()
          loadSummary()
        } else {
          message.error(result.msg || '删除预生产单失败')
        }
      } catch (error) {
        console.error('删除预生产单失败:', error)
        message.error('删除预生产单失败')
      }
    }

    onMounted(() => {
      loadFactories()
      loadOrders()
      loadSummary()
    })

    return {
      loading,
      submitting,
      exportLoading,
      summaryLoading,
      summaryData,
      completionRate,
      orderList,
      factories,
      searchForm,
      completeForm,
      pagination,
      columns,
      completeColumns,
      completeModalVisible,
      totalCompletionQuantity,
      totalCompletionAmount,
      calculateProgress,
      getStatusColor,
      getStatusText,
      getPaymentStatusText,
      handleTogglePayment,
      loadOrders,
      handleTableChange,
      handleSearch,
      handleReset,
      handleExport,
      handleViewDetail,
      handleComplete,
      calculateTotals,
      handleCompleteSubmit,
      handleCompleteCancel,
      handleCancel,
      handleDelete
    }
  }
}
</script>

<style scoped>
.preproduction-list {
  padding: 0;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.page-header h2 {
  margin: 0;
}

.summary-card {
  margin-bottom: 16px;
}

.stat-sub {
  font-size: 13px;
  color: #999;
  margin-top: 4px;
}

.filter-card {
  margin-bottom: 16px;
}

/* 内联筛选项换行时增加行间距，避免「查询/重置」按钮贴住上一行 */
.filter-card :deep(.ant-form-inline .ant-form-item) {
  margin-bottom: 12px;
}
.filter-card :deep(.ant-form-inline) {
  row-gap: 4px;
}
</style>
